import React, {type ReactNode} from 'react';

import {useRadioState, RadioGroup as BaseRadioGroup} from 'reakit/Radio';
import {useFormGroupState, useFormGroupId} from '../FormGroup';
import {getDefinedValue} from '../../utils';

import {RadioGroupView} from './RadioGroup.styles';

import type {StackProps} from '../Stack';
import type {
  HTMLAttributes,
  SpacingNumber,
  SpringTooling,
} from '../__SharedTypes';

type BaseRadioGroupProps = {
  /**
   * Custom label for assistive technology devices.
   */
  ['aria-label']?: string;
  /**
   * Custom label (associated with another element) for assistive technology devices.
   */
  ['aria-labelledby']?: string;
  /**
   * Content for RadioGroup.
   */
  children?: ReactNode;
  /**
   * The initial value of the RadioGroup.
   */
  defaultValue?: string;
  /**
   * Renders the error state for the RadioGroup.
   */
  isError?: boolean;
  /**
   * Indicates whether or not the field is required and is used to make Checkbox more accessible to assistive technologies.
   */
  isRequired?: boolean;
  /**
   * A name for the inner Radio components of RadioGroup. Use `name` when you have multiple Radios within your form
   */
  name?: string;
  /**
   * Determines how the Radio components stack as well as keyboard navigation.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * The callback when the value changes. Use `value` and `onChange` to control the value of `RadioGroup`.
   */
  onChange?: (value: string) => void;
  /**
   * The value of the RadioGroup. Use `value` and `onChange` to control the value of `RadioGroup`.
   */
  value?: string | null;
  /**
   * `RadioGroup` uses `Stack` to space `Radio` components apart.
   */
  spacing?: SpacingNumber;
} & Omit<HTMLAttributes, 'onChange'> &
  SpringTooling;

export type RadioGroupProvidierProps = BaseRadioGroupProps;

export type RadioGroupProps = BaseRadioGroupProps &
  Omit<StackProps, 'onChange' | 'direction'>;

type RadioGroupContextProps = {
  isError?: boolean;
  isRequried?: boolean;
  name?: string;
  radio: any;
};

const RadioGroupContext = React.createContext<RadioGroupContextProps>({
  radio: {},
});
export const useRadioGroup = () => React.useContext(RadioGroupContext);

const RadioGroupProvider = ({
  'aria-labelledby': ariaLabelledByProp,
  children,
  defaultValue,
  id: idProp,
  isError: isErrorProp,
  isRequired: isRequiredProp,
  name,
  onChange,
  orientation,
  value,
  ...props
}: RadioGroupProvidierProps) => {
  const id = useFormGroupId(idProp);
  const {
    ariaDescribedBy,
    isError: errorState,
    isRequired: requiredState,
    labelId,
  } = useFormGroupState();

  const ariaLabelledBy = getDefinedValue(ariaLabelledByProp, labelId);
  const isControlled = typeof value !== 'undefined';
  const isError = getDefinedValue(isErrorProp, errorState);
  const isRequired = getDefinedValue(isRequiredProp, requiredState);
  const initialValue = getDefinedValue(value, defaultValue);
  const radio = useRadioState({baseId: id, orientation, state: initialValue});

  /**
   * Intercepting the internal Reakit setState callback to fire the
   * onChange callback as part of this process.
   *
   * By doing this, we avoid setting up another "listener" via a
   * React.useEffect() hook, which may can unnecessary and unexpected
   * calls to the onChange callback.
   *
   * The wrapped setState must keep a **stable function identity** across
   * renders. Reakit's Radio puts `options.setState` in a useCallback/useEffect
   * dependency list; recreating the wrapper each render re-triggers that
   * effect while `unstable_moves` is set (keyboard navigation), which can
   * call onChange again and cause a maximum update depth loop in controlled
   * usage.
   *
   * @see https://webflow.atlassian.net/browse/PDTY-3954
   */
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const nativeSetStateRef = React.useRef(radio.setState);
  nativeSetStateRef.current = radio.setState;

  const wrappedSetStateRef = React.useRef<((next: any) => void) | null>(null);
  if (wrappedSetStateRef.current === null) {
    wrappedSetStateRef.current = (next: any) => {
      onChangeRef.current?.(next);
      nativeSetStateRef.current(next);
    };
  }
  const wrappedSetState = wrappedSetStateRef.current;

  /**
   * Reakit `useRadioState` return value must not be mutated (react-compiler).
   * Spread into a new object with intercepted `setState` and, when controlled,
   * the current `value` as `state`.
   */
  const radioForChildren = {
    ...radio,
    setState: wrappedSetState,
    ...(isControlled ? {state: value} : {}),
  };

  const contextProps = {
    isError,
    isRequired,
    radio: radioForChildren,
    name,
  } as const;

  return (
    <RadioGroupContext.Provider value={contextProps}>
      <BaseRadioGroup
        {...radioForChildren}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        aria-required={isRequired}
        {...props}
      >
        {children}
      </BaseRadioGroup>
    </RadioGroupContext.Provider>
  );
};

/**
 * # RadioGroup
 *
 * RadioGroup is a form component that makes it easier to group Radio
 * components, while following WAI-ARIA Radio Button/Group Pattern.
 *
 * ## Usage
 *
 * ```jsx
 * import {RadioGroup, Radio} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   const [value, setValue] = React.useState('crocus');
 *   return (
 *     <RadioGroup aria-label="Choose a flower" value={value} onChange={setValue}>
 *       <Radio label="Crocus" value="crocus" />
 *       <Radio label="Daffodils" value="daffodils" />
 *       <Radio label="Epimedium" value="epimedium" />
 *     </RadioGroup>
 *   );
 * };
 * ```
 *
 * @component
 */
export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      align: alignProp,
      children,
      defaultValue,
      height,
      isFullHeight,
      isFullWidth,
      justify = 'start',
      name,
      onChange,
      orientation,
      spacing = 2,
      value,
      width,
      ...props
    }: RadioGroupProps,
    ref
  ) => {
    const stackDirection = orientation === 'horizontal' ? 'row' : 'column';
    const align = alignProp
      ? alignProp
      : stackDirection === 'row'
        ? 'stretch'
        : 'start';

    return (
      <RadioGroupProvider
        defaultValue={defaultValue}
        name={name}
        onChange={onChange}
        orientation={orientation}
        value={value}
        {...props}
      >
        {/* @ts-expect-error - TS2322 - Typescript is having trouble with the discriminated union types here for width & isFullWidth. */}
        <RadioGroupView
          align={align}
          data-sc="RadioGroup"
          direction={stackDirection}
          height={height}
          isFullHeight={isFullHeight}
          isFullWidth={isFullWidth}
          justify={justify}
          ref={ref}
          spacing={spacing}
          width={width}
        >
          {children}
        </RadioGroupView>
      </RadioGroupProvider>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
RadioGroup.toString = RadioGroupView.toString;

export default RadioGroup;
